import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  MapPin,
  Briefcase,
  Utensils,
  Calendar,
  Sparkles,
  ShieldCheck,
  Compass,
  Camera,
  Heart,
  Globe,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Edit3,
  Save,
  X,
  Trash2,
  FileText,
  Share2,
  ExternalLink,
  ChevronRight,
  Plus,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import authApi from '../api/authApi';
import tripsApi from '../api/tripsApi';
import Loader from './Loader';

// Module-level caches for instant 0ms switching
let cachedProfile = null;
let cachedDashboardTrips = null;

export default function Profile() {
  const navigate = useNavigate();
  const { user: authUser, setUser } = useAuth();

  // Instant UI initialization (no blocking full-screen loader if cached or authUser present)
  const initialProfile = cachedProfile || authUser || null;
  const [profile, setProfile] = useState(initialProfile);
  const [loading, setLoading] = useState(!initialProfile);

  // Trips data
  const [draftTrips, setDraftTrips] = useState(cachedDashboardTrips?.drafts || []);
  const [sharedTrips, setSharedTrips] = useState(cachedDashboardTrips?.shared || []);
  const [loadingTrips, setLoadingTrips] = useState(!cachedDashboardTrips);

  // Instagram-style active tab: 'drafts' | 'shared'
  const [activeTab, setActiveTab] = useState('drafts');

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: 'draft', // 'draft' | 'shared'
    trip: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // Profile Edit state
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [error, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: initialProfile?.name || '',
    age: initialProfile?.age || '',
    gender: initialProfile?.gender || initialProfile?.sex || 'Male',
    profession: initialProfile?.profession || '',
    dietaryPreference: initialProfile?.dietaryPreference || 'Any',
    location: initialProfile?.location || '',
    coordinates: Array.isArray(initialProfile?.coordinates) ? initialProfile.coordinates.join(', ') : '',
    interests: Array.isArray(initialProfile?.interests) ? initialProfile.interests : [],
  });
  const [newInterestTag, setNewInterestTag] = useState('');

  // 1. Fetch Profile Data (Silent background revalidation)
  useEffect(() => {
    let isMounted = true;
    const fetchProfile = async () => {
      try {
        let data = null;
        try {
          data = await authApi.getProfile();
        } catch (e) {
          data = await authApi.getMe();
        }

        if (isMounted && data) {
          cachedProfile = data;
          setProfile(data);
          if (setUser) setUser(data);
          syncFormData(data);
        }
      } catch (err) {
        console.warn('Silent profile fetch note:', err.message);
        if (isMounted && !profile) {
          const fallback = authUser || {
            name: localStorage.getItem('userName') || 'Explorer',
            email: localStorage.getItem('userEmail') || 'traveler@syncandexplore.com',
            role: localStorage.getItem('userRole') || 'Traveller',
            avatarUrl: null,
            age: 26,
            gender: 'Male',
            profession: 'Explorer',
            dietaryPreference: 'Flexible',
            location: 'India',
            coordinates: [77.5946, 12.9716],
            interests: ['Mountain Trekking', 'Beach Escapes', 'Culinary Tours', 'Photography'],
            emailVerified: true,
          };
          setProfile(fallback);
          syncFormData(fallback);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchProfile();
    return () => {
      isMounted = false;
    };
  }, []);

  // 2. Fetch User Trips for Profile Tabs (Silent background revalidation)
  useEffect(() => {
    let isMounted = true;
    const fetchTrips = async () => {
      try {
        const data = await tripsApi.getDashboard();
        if (isMounted && data) {
          cachedDashboardTrips = data;
          setDraftTrips(data.drafts || []);
          setSharedTrips(data.shared || []);
        }
      } catch (err) {
        console.error('Failed to load user trips for profile:', err);
      } finally {
        if (isMounted) setLoadingTrips(false);
      }
    };

    fetchTrips();
    return () => {
      isMounted = false;
    };
  }, []);

  const syncFormData = (data) => {
    setFormData({
      name: data.name || '',
      age: data.age || '',
      gender: data.gender || data.sex || 'Male',
      profession: data.profession || '',
      dietaryPreference: data.dietaryPreference || 'Any',
      location: data.location || '',
      coordinates: Array.isArray(data.coordinates) ? data.coordinates.join(', ') : '',
      interests: Array.isArray(data.interests) ? data.interests : [],
    });
  };

  // Avatar upload via Cloudinary & persist
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingAvatar(true);
    setError(null);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const base64Data = event.target?.result;
          let newAvatarUrl = base64Data;
          try {
            const uploadRes = await tripsApi.uploadImage(base64Data);
            if (uploadRes?.url) newAvatarUrl = uploadRes.url;
          } catch (uploadErr) {
            console.warn('Direct Cloudinary upload fallback to data URL:', uploadErr);
          }

          try {
            await authApi.updateProfile({ avatarUrl: newAvatarUrl });
          } catch (patchErr) {
            console.warn('Profile PATCH failed, continuing with local state:', patchErr);
          }

          const updated = { ...(profile || {}), avatarUrl: newAvatarUrl };
          cachedProfile = updated;
          setProfile(updated);
          if (authUser && setUser) {
            setUser({ ...authUser, avatarUrl: newAvatarUrl });
          }
          setSaveSuccess('Profile picture updated successfully!');
          setTimeout(() => setSaveSuccess(''), 4000);
        } catch (innerErr) {
          console.error('Avatar upload failed:', innerErr);
          setError('Failed to process avatar image. Please try again.');
        } finally {
          setUploadingAvatar(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error(err);
      setUploadingAvatar(false);
    }
  };

  // Save profile edits
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setError(null);
    try {
      let coords = [];
      if (formData.coordinates) {
        coords = formData.coordinates
          .split(',')
          .map((c) => parseFloat(c.trim()))
          .filter((c) => !isNaN(c));
      }

      const payload = {
        name: formData.name.trim(),
        age: formData.age ? parseInt(formData.age, 10) : undefined,
        gender: formData.gender,
        profession: formData.profession.trim(),
        dietaryPreference: formData.dietaryPreference,
        location: formData.location.trim(),
        coordinates: coords,
        interests: formData.interests,
      };

      const updated = await authApi.updateProfile(payload);
      const merged = { ...(profile || {}), ...updated };
      cachedProfile = merged;
      setProfile(merged);
      if (setUser) setUser((prev) => ({ ...prev, ...updated }));

      setIsEditing(false);
      setSaveSuccess('Profile updated successfully!');
      setTimeout(() => setSaveSuccess(''), 4000);
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError(err.response?.data?.message || 'Failed to update profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const addInterest = () => {
    if (!newInterestTag.trim()) return;
    const tag = newInterestTag.trim().replace(/^#/, '');
    if (!formData.interests.includes(tag)) {
      setFormData((prev) => ({
        ...prev,
        interests: [...prev.interests, tag],
      }));
    }
    setNewInterestTag('');
  };

  const removeInterest = (tag) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.filter((i) => i !== tag),
    }));
  };

  // ── Trip Deletion Handler (Cascades entirely from PostgreSQL) ──
  const handleConfirmDelete = async () => {
    const { type, trip } = deleteModal;
    if (!trip || !trip.id) return;

    setIsDeleting(true);
    try {
      if (type === 'draft') {
        await tripsApi.deleteDraft(trip.id);
        const updated = draftTrips.filter((t) => t.id !== trip.id);
        setDraftTrips(updated);
        if (cachedDashboardTrips) {
          cachedDashboardTrips.drafts = updated;
        }
      } else {
        await tripsApi.deleteSharedTrip(trip.id);
        const updated = sharedTrips.filter((t) => t.id !== trip.id);
        setSharedTrips(updated);
        if (cachedDashboardTrips) {
          cachedDashboardTrips.shared = updated;
        }
      }
      setDeleteModal({ isOpen: false, type: 'draft', trip: null });
      setSaveSuccess(`${type === 'draft' ? 'Draft trip' : 'Shared expedition'} completely removed from database.`);
      setTimeout(() => setSaveSuccess(''), 4000);
    } catch (err) {
      console.error('Delete trip error:', err);
      alert('Failed to delete trip: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return <Loader text="Loading Profile..." />;
  }

  const avatarSrc = profile?.avatarUrl || '/images/sync_logo.png';
  const username = profile?.email
    ? profile.email.split('@')[0].toLowerCase().replace(/[^a-z0-9._]/g, '')
    : (profile?.name || 'explorer').toLowerCase().replace(/\s+/g, '_');
  const isVerified = profile?.emailVerified !== false;

  const totalTripsCount = (sharedTrips.length || 0) + (draftTrips.length || 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-8 bg-slate-50 min-h-screen">
      {/* ───────────────────────────────────────────────────────────────── */}
      {/* INSTAGRAM-STYLE PROFILE HEADER CARD                               */}
      {/* ───────────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-3xl p-6 sm:p-10 border border-gray-200 shadow-sm relative overflow-hidden">
        {/* Decorative background glow */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-bl from-teal-100/50 via-emerald-50/30 to-transparent rounded-full blur-3xl pointer-events-none -mr-20 -mt-20"></div>

        <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-12">
          {/* Avatar with Instagram-style Story Ring & Upload Badge */}
          <div className="relative shrink-0">
            <div className="p-[3.5px] bg-gradient-to-tr from-[#2A9D8F] via-[#E76F51] to-[#F4A261] rounded-full shadow-lg">
              <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full overflow-hidden border-4 border-white bg-slate-100 flex items-center justify-center">
                <img
                  src={avatarSrc}
                  alt={profile?.name || 'Profile'}
                  onError={(e) => {
                    e.target.src = '/images/sync_logo.png';
                  }}
                  className={`w-full h-full ${profile?.avatarUrl ? 'object-cover' : 'object-contain p-3'}`}
                />
              </div>
            </div>

            {/* Cloudinary Upload Action */}
            <label
              htmlFor="avatar-upload"
              className="absolute bottom-1 right-1 w-9 h-9 bg-[#2A9D8F] hover:bg-[#238177] text-white rounded-full flex items-center justify-center cursor-pointer shadow-md hover:scale-105 transition-all border-2 border-white"
              title="Change Profile Photo"
            >
              {uploadingAvatar ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Camera className="w-4 h-4" />
              )}
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
                disabled={uploadingAvatar}
              />
            </label>
          </div>

          {/* User Information & Instagram Stats Bar */}
          <div className="flex-1 text-center md:text-left space-y-4 w-full">
            {/* Handle, Verified Badge & Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-start">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight font-mono">
                  @{username}
                </h1>
                {isVerified && (
                  <span
                    className="inline-flex items-center text-[#2A9D8F]"
                    title="Verified Traveler"
                  >
                    <ShieldCheck className="w-5 h-5 fill-[#2A9D8F] text-white" />
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(!isEditing);
                    syncFormData(profile);
                  }}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  <span>{isEditing ? 'Close Edit' : 'Edit Profile'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/create-trip')}
                  className="px-4 py-2 bg-[#2A9D8F] hover:bg-[#238177] text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>New Trip</span>
                </button>
              </div>
            </div>

            {/* Instagram 3-Pill Stats Counter */}
            <div className="flex items-center justify-center md:justify-start gap-8 sm:gap-12 py-2 border-y border-gray-100 text-sm">
              <div className="flex items-center gap-1.5">
                <span className="font-extrabold text-gray-900 text-base">{totalTripsCount}</span>
                <span className="text-gray-500 font-medium">expeditions</span>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('drafts')}
                className="flex items-center gap-1.5 hover:opacity-80 transition-opacity cursor-pointer"
              >
                <span className="font-extrabold text-[#2A9D8F] text-base">{draftTrips.length}</span>
                <span className={`font-medium ${activeTab === 'drafts' ? 'text-gray-900 font-bold underline decoration-2 decoration-[#2A9D8F]' : 'text-gray-500'}`}>
                  drafts
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('shared')}
                className="flex items-center gap-1.5 hover:opacity-80 transition-opacity cursor-pointer"
              >
                <span className="font-extrabold text-[#E76F51] text-base">{sharedTrips.length}</span>
                <span className={`font-medium ${activeTab === 'shared' ? 'text-gray-900 font-bold underline decoration-2 decoration-[#E76F51]' : 'text-gray-500'}`}>
                  shared
                </span>
              </button>
            </div>

            {/* Bio & Specifications Section */}
            <div className="space-y-2 text-xs sm:text-sm text-gray-700">
              <div className="font-bold text-gray-900 text-base flex items-center justify-center md:justify-start gap-2">
                <span>{profile?.name || 'Explorer'}</span>
                {profile?.role && (
                  <span className="px-2 py-0.5 bg-teal-50 text-[#2A9D8F] rounded-md text-[11px] font-bold uppercase tracking-wider border border-teal-100">
                    {profile.role}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-4 gap-y-1 text-gray-600 text-xs">
                {profile?.profession && (
                  <span className="flex items-center gap-1">
                    <Briefcase className="w-3.5 h-3.5 text-[#2A9D8F]" />
                    <span>{profile.profession}</span>
                  </span>
                )}
                {profile?.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-rose-500" />
                    <span>{profile.location}</span>
                  </span>
                )}
                {profile?.dietaryPreference && (
                  <span className="flex items-center gap-1">
                    <Utensils className="w-3.5 h-3.5 text-amber-500" />
                    <span>{profile.dietaryPreference}</span>
                  </span>
                )}
                {profile?.age && (
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5 text-blue-500" />
                    <span>{profile.age} yrs ({profile?.gender || 'Explorer'})</span>
                  </span>
                )}
              </div>

              {/* Travel Interest Hashtags */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-1.5 pt-1">
                {(profile?.interests && profile.interests.length > 0
                  ? profile.interests
                  : ['MountainTrekking', 'BeachEscapes', 'Photography', 'Culinary']
                ).map((tag, idx) => (
                  <span
                    key={idx}
                    className="text-[#2A9D8F] hover:text-[#238177] font-semibold text-xs transition-colors cursor-default"
                  >
                    #{tag.replace(/\s+/g, '')}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Feedback Alerts */}
        {saveSuccess && (
          <div className="mt-5 p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-2xl flex items-center gap-2 animate-in fade-in">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span className="font-semibold">{saveSuccess}</span>
          </div>
        )}

        {error && (
          <div className="mt-5 p-3.5 bg-red-50 border border-red-200 text-red-800 text-xs rounded-2xl flex items-center gap-2 animate-in fade-in">
            <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
            <span className="font-semibold">{error}</span>
          </div>
        )}
      </div>

      {/* ───────────────────────────────────────────────────────────────── */}
      {/* EDIT PROFILE FORM (MODAL / INLINE ACCORDION)                      */}
      {/* ───────────────────────────────────────────────────────────────── */}
      {isEditing && (
        <form
          onSubmit={handleSaveProfile}
          className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-6 animate-in fade-in"
        >
          <div className="border-b border-gray-100 pb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Edit Profile Details</h3>
              <p className="text-xs text-gray-500">Update your traveler profile and AI personalization preferences</p>
            </div>
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                Full Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#2A9D8F]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                Age
              </label>
              <input
                type="number"
                min="13"
                max="120"
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#2A9D8F]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                Gender
              </label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#2A9D8F]"
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Non-Binary">Non-Binary</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                Profession / Occupation
              </label>
              <input
                type="text"
                value={formData.profession}
                onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                placeholder="e.g. Software Engineer, Designer"
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#2A9D8F]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                Dietary Preference
              </label>
              <select
                value={formData.dietaryPreference}
                onChange={(e) => setFormData({ ...formData, dietaryPreference: e.target.value })}
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#2A9D8F]"
              >
                <option value="Any">Any / Flexible</option>
                <option value="Strictly Vegetarian">Strictly Vegetarian</option>
                <option value="Vegan">Vegan</option>
                <option value="Jain">Jain</option>
                <option value="Halal">Halal</option>
                <option value="Non-Vegetarian">Non-Vegetarian</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                Primary Location / City
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g. Bengaluru, India"
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#2A9D8F]"
              />
            </div>

            <div className="sm:col-span-2 lg:col-span-3">
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                GPS Coordinates (Longitude, Latitude)
              </label>
              <input
                type="text"
                value={formData.coordinates}
                onChange={(e) => setFormData({ ...formData, coordinates: e.target.value })}
                placeholder="e.g. 77.5946, 12.9716"
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#2A9D8F]"
              />
            </div>

            {/* Travel Interests Builder */}
            <div className="sm:col-span-2 lg:col-span-3 space-y-2">
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                Travel Passions &amp; Interests
              </label>
              <div className="flex flex-wrap items-center gap-2">
                {formData.interests.map((tag, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-50 text-[#2A9D8F] border border-teal-200 rounded-xl text-xs font-bold"
                  >
                    <span>#{tag}</span>
                    <button
                      type="button"
                      onClick={() => removeInterest(tag)}
                      className="hover:text-rose-600 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-2 max-w-md pt-1">
                <input
                  type="text"
                  value={newInterestTag}
                  onChange={(e) => setNewInterestTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addInterest();
                    }
                  }}
                  placeholder="Add interest (e.g. Trekking, Food)"
                  className="flex-1 px-4 py-2 bg-white rounded-xl border border-gray-200 text-xs focus:outline-none focus:border-[#2A9D8F]"
                />
                <button
                  type="button"
                  onClick={addInterest}
                  className="px-4 py-2 bg-teal-50 text-[#2A9D8F] font-bold text-xs rounded-xl border border-teal-200 hover:bg-teal-100 transition-colors"
                >
                  Add Tag
                </button>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-5 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-bold text-xs rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={savingProfile}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#2A9D8F] hover:bg-[#238177] text-white font-bold text-xs rounded-xl shadow-sm transition-all cursor-pointer"
            >
              {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span>Save Changes</span>
            </button>
          </div>
        </form>
      )}

      {/* ───────────────────────────────────────────────────────────────── */}
      {/* INSTAGRAM-STYLE SEGMENTED TABS (DRAFT TRIPS & SHARED TRIPS)       */}
      {/* ───────────────────────────────────────────────────────────────── */}
      <div className="space-y-6">
        {/* Navigation Tabs Bar */}
        <div className="flex items-center justify-center border-t border-gray-200">
          <div className="flex items-center gap-8 sm:gap-16">
            {/* Draft Trips Tab */}
            <button
              type="button"
              onClick={() => setActiveTab('drafts')}
              className={`flex items-center gap-2 py-4 text-xs font-bold uppercase tracking-wider transition-all border-t-2 -mt-[2px] cursor-pointer ${
                activeTab === 'drafts'
                  ? 'border-[#2A9D8F] text-[#2A9D8F]'
                  : 'border-transparent text-gray-400 hover:text-gray-700'
              }`}
            >
              <FileText className="w-4 h-4" />
              <span>Draft Trips</span>
              <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] bg-teal-50 text-[#2A9D8F] border border-teal-100">
                {draftTrips.length}
              </span>
            </button>

            {/* Shared Expeditions Tab */}
            <button
              type="button"
              onClick={() => setActiveTab('shared')}
              className={`flex items-center gap-2 py-4 text-xs font-bold uppercase tracking-wider transition-all border-t-2 -mt-[2px] cursor-pointer ${
                activeTab === 'shared'
                  ? 'border-[#E76F51] text-[#E76F51]'
                  : 'border-transparent text-gray-400 hover:text-gray-700'
              }`}
            >
              <Share2 className="w-4 h-4" />
              <span>Shared Expeditions</span>
              <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] bg-rose-50 text-[#E76F51] border border-rose-100">
                {sharedTrips.length}
              </span>
            </button>
          </div>
        </div>

        {/* ── TAB 1: DRAFT TRIPS GRID ── */}
        {activeTab === 'drafts' && (
          <div>
            {loadingTrips ? (
              <div className="text-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#2A9D8F] mx-auto mb-2" />
                <p className="text-xs text-gray-400">Loading your draft itineraries...</p>
              </div>
            ) : draftTrips.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-gray-200 p-8 space-y-4">
                <div className="w-16 h-16 bg-teal-50 text-[#2A9D8F] rounded-full flex items-center justify-center mx-auto">
                  <FileText className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">No Draft Trips Found</h3>
                  <p className="text-xs text-gray-500 max-w-sm mx-auto mt-1">
                    When you customize itineraries with AI or manual builder and save them for later, they will appear here.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/create-trip')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#2A9D8F] hover:bg-[#238177] text-white rounded-xl text-xs font-bold shadow-sm transition-all cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create New Trip</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {draftTrips.map((draft) => {
                  const coverImg =
                    draft.coverImageUrl ||
                    draft.summary?.imageUrl ||
                    'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=800&auto=format&fit=crop';
                  const title = draft.title || `${draft.destination || 'Expedition'} Itinerary`;
                  const daysCount = draft.totalDays || (draft.days ? draft.days.length : 1);
                  const budgetNum = Number(draft.budget || 0);

                  return (
                    <div
                      key={draft.id}
                      onClick={() => navigate(`/dashboard/trip/${draft.id}?type=draft`)}
                      className="group bg-white rounded-3xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between cursor-pointer"
                    >
                      {/* Image Frame */}
                      <div className="relative h-48 overflow-hidden bg-slate-100">
                        <img
                          src={coverImg}
                          alt={draft.destination}
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=800&auto=format&fit=crop';
                          }}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3 px-3 py-1 bg-amber-500/90 backdrop-blur-md rounded-full text-white text-[11px] font-bold shadow-sm">
                          Draft Itinerary
                        </div>
                        <div className="absolute top-3 right-3 px-2.5 py-1 bg-black/60 backdrop-blur-md rounded-full text-white text-[11px] font-bold">
                          {daysCount} Days
                        </div>
                      </div>

                      {/* Content Details */}
                      <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                        <div>
                          <div className="flex items-center gap-1 text-[#2A9D8F] text-xs font-bold">
                            <MapPin className="w-3.5 h-3.5" />
                            <span>{draft.destination || 'Destination'}</span>
                          </div>
                          <h4 className="text-base font-bold text-gray-900 mt-1 line-clamp-1 group-hover:text-[#2A9D8F] transition-colors">
                            {title}
                          </h4>
                          {budgetNum > 0 && (
                            <p className="text-xs font-semibold text-emerald-600 mt-1">
                              Budget: ₹{budgetNum.toLocaleString()}
                            </p>
                          )}
                        </div>

                        {/* Action Buttons: Continue & Delete */}
                        <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/create-trip?draftId=${draft.id}`);
                            }}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-teal-50 hover:bg-[#2A9D8F] text-[#2A9D8F] hover:text-white rounded-xl text-xs font-bold transition-all flex-1 justify-center cursor-pointer"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                            <span>Continue</span>
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteModal({
                                isOpen: true,
                                type: 'draft',
                                trip: draft,
                              });
                            }}
                            className="p-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-xl transition-colors cursor-pointer"
                            title="Delete Draft Entirely From Database"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── TAB 2: SHARED TRIPS GRID ── */}
        {activeTab === 'shared' && (
          <div>
            {loadingTrips ? (
              <div className="text-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#E76F51] mx-auto mb-2" />
                <p className="text-xs text-gray-400">Loading your shared expeditions...</p>
              </div>
            ) : sharedTrips.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-gray-200 p-8 space-y-4">
                <div className="w-16 h-16 bg-rose-50 text-[#E76F51] rounded-full flex items-center justify-center mx-auto">
                  <Share2 className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">No Shared Expeditions Yet</h3>
                  <p className="text-xs text-gray-500 max-w-sm mx-auto mt-1">
                    Published journeys and expeditions shared with travel companions will appear here.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/create-trip')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#2A9D8F] hover:bg-[#238177] text-white rounded-xl text-xs font-bold shadow-sm transition-all cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  <span>Architect Journey</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {sharedTrips.map((trip) => {
                  const coverImg =
                    trip.coverImageUrl ||
                    trip.summary?.imageUrl ||
                    'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=800&auto=format&fit=crop';
                  const title = trip.title || `${trip.destination || 'Expedition'} Adventure`;
                  const daysCount = trip.totalDays || (trip.days ? trip.days.length : 1);
                  const budgetNum = Number(trip.budget || 0);

                  return (
                    <div
                      key={trip.id}
                      onClick={() => navigate(`/dashboard/trip/${trip.id}?type=shared`)}
                      className="group bg-white rounded-3xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between cursor-pointer"
                    >
                      {/* Image Frame */}
                      <div className="relative h-48 overflow-hidden bg-slate-100">
                        <img
                          src={coverImg}
                          alt={trip.destination}
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=800&auto=format&fit=crop';
                          }}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3 px-3 py-1 bg-[#2A9D8F]/90 backdrop-blur-md rounded-full text-white text-[11px] font-bold shadow-sm">
                          Shared Trip
                        </div>
                        <div className="absolute top-3 right-3 px-2.5 py-1 bg-black/60 backdrop-blur-md rounded-full text-white text-[11px] font-bold">
                          {daysCount} Days
                        </div>
                      </div>

                      {/* Details */}
                      <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                        <div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="flex items-center gap-1 text-[#2A9D8F] font-bold">
                              <MapPin className="w-3.5 h-3.5" />
                              <span>{trip.destination || 'Destination'}</span>
                            </span>
                            {trip.inviteCode && (
                              <span className="font-mono text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-md font-bold">
                                {trip.inviteCode}
                              </span>
                            )}
                          </div>
                          <h4 className="text-base font-bold text-gray-900 mt-1.5 line-clamp-1 group-hover:text-[#2A9D8F] transition-colors">
                            {title}
                          </h4>
                          {budgetNum > 0 && (
                            <p className="text-xs font-semibold text-emerald-600 mt-1">
                              Budget: ₹{budgetNum.toLocaleString()}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/create-trip?sharedTripId=${trip.id}`);
                            }}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-teal-50 hover:bg-[#2A9D8F] text-[#2A9D8F] hover:text-white rounded-xl text-xs font-bold transition-all flex-1 justify-center cursor-pointer"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                            <span>Edit</span>
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteModal({
                                isOpen: true,
                                type: 'shared',
                                trip: trip,
                              });
                            }}
                            className="p-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-xl transition-colors cursor-pointer"
                            title="Delete Shared Trip Entirely From Database"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ───────────────────────────────────────────────────────────────── */}
      {/* CASCADING PURGE DELETION CONFIRMATION MODAL                       */}
      {/* ───────────────────────────────────────────────────────────────── */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-gray-200 space-y-5 animate-in zoom-in-95">
            <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center">
              <Trash2 className="w-6 h-6" />
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-black text-gray-900">
                Permanently Delete {deleteModal.type === 'draft' ? 'Draft Trip' : 'Shared Trip'}?
              </h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                This will completely remove <strong className="text-gray-900 font-bold">"{deleteModal.trip?.title || deleteModal.trip?.destination || 'this trip'}"</strong> along with all associated itinerary days, activities, routes, and database records. This action cannot be undone.
              </p>
            </div>

            <div className="pt-2 flex items-center justify-end gap-3">
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => setDeleteModal({ isOpen: false, type: 'draft', trip: null })}
                className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={handleConfirmDelete}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold shadow-md transition-all cursor-pointer"
              >
                {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                <span>{isDeleting ? 'Deleting...' : 'Delete Entirely'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
